import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { IrisDMSFolder, IrisDMSFolderI, IrisDmsRecentFilesFolder, IrisDmsRootFolderId } from '../models/IrisDMSFolder';
import { removeAllEntities, removeEntities, removeEntity, setAllEntities, setEntities, setEntity, updateAllEntities, updateEntity, withEntities } from '@ngrx/signals/entities';
import { setError, setLoaded, setLoading, withRequestState } from '@iris/common/signals/features/request-status.feature';
import { computed, inject, Signal } from '@angular/core';
import { IrisFoldersService } from '@iris/common/services/docs/folders.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, mergeMap, pipe, switchMap, tap, throwError } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { mapFoldersToLine, expandTree, getFolderPathByFolderId } from '../helpers/dms-folders.helper';
import { Store } from '@ngrx/store';
import { ReloadCurrentPage, SetSelectedFolder } from '../sandbox/actions/dms-global.actions';
import { IrisDmsSystemFoldersStore } from './system-folders.signal-store';
import { IrisDmsFolderRequestStatusStore } from './folder-request-status.signal-store';
import {  RemoveFolderSuccess, UpdateFolderSuccess } from '../sandbox/actions/folders.actions';
import { chain } from 'lodash';
import { IrisDmsGlobalStore } from './dms-global.signal-store';
import { IrisDmsBulkActionsService } from '../services/dms-bulk-actions.service';

export interface IrisDmsFoldersState {
  isModalView: boolean;
  savedFoldersState: IrisDMSFolderI[];
  excludedFolders: string[];
  wrongRequestedFolderId: string;
}

export const initialState: IrisDmsFoldersState = {
  isModalView: false,
  savedFoldersState: [],
  excludedFolders: [],
  wrongRequestedFolderId: null,
};

export const IrisDmsFoldersStore = signalStore(
  { providedIn: 'root' },
  withEntities<IrisDMSFolderI>(),
  withState<IrisDmsFoldersState>(initialState),
  withRequestState(),
  withComputed(store => ({
    hasFolders: computed(() => !!store.entities().length),
  })),
  withMethods(store => ({
    getFolderById: (folderId: string) => computed(() => {
      if (folderId === IrisDmsRecentFilesFolder.id) {
        return IrisDmsRecentFilesFolder;
      }
      return store.entityMap()[folderId];
    }),
    getFoldersByIds: (foldersIds: string[]) => store.entities().filter(entitiy => foldersIds.includes(entitiy.id)),
    mapFoldersToLine: (folders: IrisDMSFolderI[]) => mapFoldersToLine(folders, store.entityMap()),
    expandFolder: (folder: IrisDMSFolderI) => {
      const expandedFolder = new IrisDMSFolder({ ...folder, expanded: true });
      patchState(store, setEntities(mapFoldersToLine([expandedFolder], store.entityMap())));
    },
    collapseFolder: (folderId: string) => {
      patchState(store, updateEntity({ id: folderId, changes: { expanded: false } }));
    },
    collapseAllFolders: () => patchState(store, updateAllEntities({ expanded: false })),
    removeFolder: (folderId: string) => {
      const folderToRemove = store.entityMap()[folderId];
      if (folderToRemove) {
        const parentFolder = store.entityMap()[folderToRemove.parentId];

        if (parentFolder?.children) {
          const parentFolderChildren = parentFolder.children.filter(({ id }) => id !== folderId);
          const folderChanges = {
            children: parentFolderChildren,
            hasChildren: !!parentFolderChildren.length,
          };
          
          patchState(store, updateEntity({ id: parentFolder.id, changes: folderChanges }));
        }
        patchState(store, removeEntity(folderId));
      }
    },
    removeFolders: (foldersIds: string[]) => {
      const foldesrUpdates = chain(foldersIds)
        .map(folderId => store.entityMap()[folderId])
        .filter(Boolean)
        .uniqBy('parentId')
        .map(folderToRemove => store.entityMap()[folderToRemove.parentId])
        .map(parentFolder => {
          const children = store.entities().filter(folder => folder.parentId === parentFolder.id && !foldersIds.includes(folder.id));
          const hasChildren = !!children.length;
          return updateEntity<IrisDMSFolderI>({ id: parentFolder.id, changes: { hasChildren, expanded: hasChildren && parentFolder.expanded } });
        })
        .value();
      patchState(store, removeEntities(foldersIds), ...foldesrUpdates);
    },
    getFoldersByParentId: (parentId: string, currentProjectId = 0, isModalBackground = false): Signal<IrisDMSFolderI[]> => computed(() => {
      return getFoldersByParentId(store.entities(), parentId)
        .filter((folder) => {
          let showFolder = true;
          if (store.isModalView() && !isModalBackground) {
            showFolder = showFolder && !(store.excludedFolders() || []).includes(folder.id);
          }
          if (currentProjectId) {
            showFolder = showFolder && folder.projectId === currentProjectId;
          }
          return showFolder;
        })
        .map(folder => new IrisDMSFolder(folder));
    }),
    getFolderPathByFolderId: (folderId: string) => computed(() => getFolderPathByFolderId(folderId, store.entityMap())),
    resetWrongRequestedFolderId: () => patchState(store, { wrongRequestedFolderId: null }),
    reset: () => patchState(store, removeAllEntities()),
  })),
  withMethods((
    store,
    foldersService = inject(IrisFoldersService),
    dmsSystemFoldersStore = inject(IrisDmsSystemFoldersStore),
    dmsFolderRequestStatusStore = inject(IrisDmsFolderRequestStatusStore),
  ) => ({
    fetchProjectRootFolder$: rxMethod<number>(
      pipe(
        switchMap(projectId => {
          patchState(store, setLoading());
          const projectChanged = store.entities().some(entitiy => entitiy.projectId === projectId);
          if (projectChanged || !projectId) {
            store.reset();
          }
          return foldersService.getProjectRootFolder(projectId).pipe(
            tapResponse({
              next: folder => {
                const folderToSet = new IrisDMSFolder({ ...store.entityMap()[folder.id] ?? {}, ...folder });
                patchState(store, setEntity(folderToSet), setLoaded());
              },
              error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
    fetchFolderById$: rxMethod<string>(
      pipe(
        filter(folderId => folderId !== IrisDmsRecentFilesFolder.id),
        mergeMap(folderId => {
          return foldersService.getFolderById(folderId).pipe(
            tapResponse({
              next: folder => {
                if (folder.isSystem) {
                  return dmsSystemFoldersStore.addFolder(folder);
                }
                patchState(store, setEntities(store.mapFoldersToLine([folder])));
              },
              error: (err: HttpErrorResponse) => patchState(store, setError(err.message), { wrongRequestedFolderId: folderId }),
            }),
          );
        }),
      ),
    ),
    expandFolder$: rxMethod<string>(
      pipe(
        tap((folderId) => dmsFolderRequestStatusStore.setInProgress(folderId)),
        mergeMap(folderId => foldersService.getFolderById(folderId).pipe(
          tapResponse({
            next: folder => store.expandFolder(folder),
            error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
            complete: () => dmsFolderRequestStatusStore.setSuccess(folderId),
          }),
        )),
      ),
    ),
    fetchParentFolders$: rxMethod<{ folderId: string; expandFoldersTree?: boolean }>(
      pipe(
        mergeMap(({ folderId, expandFoldersTree }) => {
          return foldersService.getParentFolders(folderId).pipe(
            tapResponse({
              next: folder => {
                const folders = store.mapFoldersToLine([folder]);
                const folderToExpand = folders.find(({ id }) => id === folderId);
                const fodlerIdToExpand = expandFoldersTree ? folderToExpand.id : folderToExpand.parentId;
                patchState(store, setEntities(expandTree(folders, fodlerIdToExpand)));
                if (store.isModalView()) {
                  patchState(store, { savedFoldersState: store.entities() });
                }
              },
              error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
    setFolders: (folders: IrisDMSFolderI[]) => {
      patchState(store, setAllEntities(store.mapFoldersToLine(folders)));
    },
  })),
  withMethods((
    store,
    foldersService = inject(IrisFoldersService),
    dmsBulkActionsService = inject(IrisDmsBulkActionsService),
    dmsFolderRequestStatusStore = inject(IrisDmsFolderRequestStatusStore),
    dmsGlobalStore = inject(IrisDmsGlobalStore),
    dmsStore = inject(Store),
  ) => ({
    createFolder$: rxMethod<{
      folder: Partial<IrisDMSFolderI>;
      parentFolderId: string;
      setSelected: boolean;
    }>(
      pipe(
        switchMap(({ folder, parentFolderId, setSelected }) => {
          return foldersService.createFolder({ ...folder, parentId: parentFolderId }).pipe(
            tapResponse({
              next: createdFolder => {
                const selectedFolderId = dmsGlobalStore.selectedFolderId();
                store.fetchParentFolders$({ folderId: createdFolder.id, expandFoldersTree: false });
                if (setSelected) {
                  const isModalView = store.isModalView();
                  if (isModalView) {
                    dmsGlobalStore.setSelectedModalFolderId(createdFolder.id);
                  } else {
                    dmsStore.dispatch(SetSelectedFolder({ folderId: createdFolder.id }));
                  }
                } else if(selectedFolderId === parentFolderId) {
                  dmsStore.dispatch(ReloadCurrentPage());
                }
              },
              error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
    updateFolder$: rxMethod<IrisDMSFolderI>(
      pipe(
        switchMap(folder => foldersService.updateFolder(folder).pipe(
          tapResponse({
            next: updatedFolder => {
              dmsStore.dispatch(UpdateFolderSuccess({ folder: updatedFolder }));
              store.expandFolder$(updatedFolder.id);
              patchState(store, updateEntity({ id: updatedFolder.id, changes: updatedFolder }));
            },
            error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
          }),
        )),
      ),
    ),
    removeFolder$: rxMethod<{ folderId: string; silence?: boolean }>(
      pipe(
        switchMap(({ folderId, silence }) => {
          const folderToRemove = store.getFolderById(folderId)();
          dmsFolderRequestStatusStore.setInProgress(folderToRemove.parentId);
          return dmsBulkActionsService.removeEntities(folderToRemove.parentId, [folderId], []).pipe(
            tapResponse({
              next: () => {
                if (!silence) {
                  dmsStore.dispatch(RemoveFolderSuccess({ removedFolder: folderToRemove }));
                }
                store.removeFolder(folderId);
              },
              error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
    moveFolder$: rxMethod<{
      folderId: string;
      targetFolderId: string;
      undo: boolean;
      silence: boolean;
      selectTargetFolder: boolean;
    }>(
      pipe(
        switchMap(action => {
          dmsFolderRequestStatusStore.setInProgress(action.targetFolderId);
          const folderToMove = store.getFolderById(action.folderId)();
          return dmsBulkActionsService.moveEntities(folderToMove.parentId, action.targetFolderId, [action.folderId], []).pipe(
            tapResponse({
              next: () => {
                dmsFolderRequestStatusStore.setSuccess(action.targetFolderId);
                const sourceFolderChildren = (store.getFoldersByParentId(folderToMove.parentId)() || [])
                  .filter(folder => folder.id !== folderToMove.id);
                const sourceFolderChanges = {
                  hasChildren: !!sourceFolderChildren.length,
                };
                patchState(
                  store,
                  updateEntity({ id: folderToMove.parentId, changes: sourceFolderChanges }),
                  updateEntity({ id: action.folderId, changes: { parentId: action.targetFolderId } }),
                );
                store.fetchParentFolders$({ folderId: action.folderId, expandFoldersTree: true });
              },
              error: (err: HttpErrorResponse) => {
                dmsFolderRequestStatusStore.setError(action.targetFolderId);
                patchState(store, setError(err.message));
                return throwError(() => err);
              },
            }),
          );
        }),
      ),
    ),
    copyFolder$: rxMethod<{ folderId: string; sourceFolderId: string; targetFolderId: string }>(
      pipe(
        switchMap(action => {
          dmsFolderRequestStatusStore.setInProgress(action.targetFolderId);
          return dmsBulkActionsService.copyEntities(action.sourceFolderId, action.targetFolderId, [action.folderId], []).pipe(
            tapResponse({
              next: () => {
                store.fetchParentFolders$({ folderId: action.targetFolderId, expandFoldersTree: true });
                dmsFolderRequestStatusStore.setSuccess(action.targetFolderId);
                const selectedFolderId = dmsGlobalStore.selectedFolderId();
                if (!!selectedFolderId && selectedFolderId === action.targetFolderId) {
                  dmsStore.dispatch(ReloadCurrentPage());
                }
              },
              error: (err: HttpErrorResponse) => {
                dmsFolderRequestStatusStore.setError(action.targetFolderId);
                patchState(store, setError(err.message));
              },
            }),
          );
        }),
      ),
    ),
    fetchFolders$: rxMethod<string[]>(
      pipe(
        switchMap(foldersIds => foldersIds.map(folderId => store.fetchFolderById$(folderId))),
      ),
    ),
    buildVisibleFlatFoldersTree: (currentProjectId = 0, isModalBackground = false): Signal<IrisDMSFolderI[]> => computed(() => {
      const rootFolders = store.getFoldersByParentId(IrisDmsRootFolderId, currentProjectId, isModalBackground)();
      const entities = store.entities();
      const excludedFolders = (store.excludedFolders() ?? []);
      const folders = chain(rootFolders).map(folder => [folder, ...getFlatFoldersTree(entities, folder, excludedFolders)]).flatten().value();
      if (!store.isModalView() && !!rootFolders.length) {
        folders.unshift(IrisDmsRecentFilesFolder);
      }
      return folders;
    }),
    modalViewShown: (foldersToExclude: string[]) => {
      if (store.isModalView()) { return; }
      patchState(store, { savedFoldersState: store.entities(), isModalView: true, excludedFolders: foldersToExclude });
      store.collapseAllFolders();
    },
    modalViewHidden: () => {
      if (!store.isModalView()) { return; }
      patchState(
        store,
        setAllEntities(store.savedFoldersState()),
        {
          savedFoldersState: [],
          isModalView: false,
          excludedFolders: [],
        },
      );
    },
  })),
);

function getFoldersByParentId(folders: IrisDMSFolderI[], parentId: string): IrisDMSFolderI[] {
  return chain(folders).filter(folder => folder.parentId === parentId).sortBy('name').value();
}

function getFlatFoldersTree(folders: IrisDMSFolderI[], parentFolder: IrisDMSFolderI, excludedFolders = []): IrisDMSFolderI[] {
  if (!parentFolder.hasChildren || !parentFolder.expanded) { return []; }
  const children = getFoldersByParentId(folders, parentFolder.id);
  return chain(children)
    .filter(child => !excludedFolders.includes(child.id))
    .map(child => [child, ...getFlatFoldersTree(folders, child, excludedFolders)])
    .flatten()
    .value();
}
